import {Button, Col, ColorPicker, Flex, Form, Input, Row, theme, notification } from 'antd'
import React, { useEffect, useState, useRef } from 'react';
import { useSelector } from 'react-redux';
import { COLOR } from '../../../theme/constant';
import CompanySettingsAPI from '../../../restAPI/CompanySettingsAPI';
import UserProfileAPI from '../../../restAPI/UserProfileAPI';
import validateForm from '../../../utils/FormValidation';
import validationRules from '../../../assets/validation/rules'; 
import CommonSkeletonLoader from '../../../helperComponents/CommonSkeleton';
import useCurrentCompany from '../../../hooks/useCurrentCompany';
const { TextArea } = Input;

//  Function to generate color presets from the COLOR.js file
const genPresets = (colors) => {
  return Object.keys(colors).reduce((acc, colorKey) => {
    if (colorKey.endsWith('900')) {
      acc.push(colors[colorKey]); // Add only the colors ending with '900'
    }
    return acc;
  }, []);
};

export default function CompanySetting() {
  const [firstForm] = Form.useForm();
  const [secondForm] = Form.useForm();
  const [thirdForm] = Form.useForm();
  const { companyId, token, userCompany } = useSelector(state => state.auth);
  const { token: { colorBgContainer, borderRadiusLG, colorPrimary }, } = theme.useToken();
  const presets = genPresets(COLOR);
  const [open, setOpen] = useState(false);
  const [editMode, setEditMode] = useState(false); // State for edit mode
  const [payload, setPayload] = useState({
    companyName: "",
    companyPhoneNumber: "",
    address: "",
	  timeZone: "",
	  companyColor: colorPrimary,
    passwordExpirePeriod: 0,
  });
  const initialized = useRef(false);  
  const currentCompanyInfo = useCurrentCompany();
  const [email, setEmail] = useState("");
  const [isModified, setIsModified] = useState(false);
  const [errors, setErrors] = useState({});
  const [isSaveButtonLoading, setIsSaveButtonLoading] = useState(false);
  const [isCompanyDataLoading, setCompanyDataLoading] = useState(false);

  const [api, contextHolder] = notification.useNotification();
    const openNotificationWithIcon = (type, message, description, placement) => {
      api[type]({
          message: message,
          description: description,
          placement
      });
    };

  const getProfileData = async (companyInfo) => {     
    if(!companyInfo) return;
    await UserProfileAPI.getUserProfileByUserDetailsId(companyInfo?.userDetailsId, companyId, token).then((res) => {
        if (res?.data) {
          setEmail(res.data.email);
          firstForm.setFieldValue("email", res?.data?.email || 'N/A');
        }
    }).catch(err => {
        console.log("error getting email :", err.message);
    })
  };

 

  useEffect(() => {
    if (!initialized.current && currentCompanyInfo) {
      setCompanyDataLoading(true)
      initialized.current = true
      const companyInfo = userCompany?.companyData?.find(company => company.companyId == companyId);      
      getProfileData(companyInfo);
      getCompanySettingsData(companyInfo);
    }
  }, [companyId]);

  const handleCancelChange = () => {
    setEditMode(!editMode); // Toggle edit mode
    if(isModified){
      setCompanyDataLoading(true);
      getCompanySettingsData(currentCompanyInfo);
      setIsModified(false);
    }
  }

  const handleSaveChange = async () => {
    if (isModified) {
      setIsSaveButtonLoading(true);
      try {
        await firstForm.validateFields();
        await thirdForm.validateFields();
        const values1 = firstForm.getFieldsValue();
        const values2 = secondForm.getFieldsValue();
        const values3 = thirdForm.getFieldsValue();
        
        const changeContent = {
          name: values1.companyName,
          address: values1.address,
          phoneNumber: values1.companyPhoneNumber,
          themeColor: values2.companyColor,
          timezone: values2.timeZone,
          passwordExpirePeriod: values3.passwordExpirePeriod,
        };
        await CompanySettingsAPI.updateCompanySettings(changeContent, currentCompanyInfo.userDetailsId, companyId, token)
            .then((response) => {
              setIsSaveButtonLoading(false);
              openNotificationWithIcon("success", "Successful", `${response.message}`,"bottomLeft");
            })
            .catch((err) => {
              setIsSaveButtonLoading(false);
              if (err.response) {
                openNotificationWithIcon("error", "Company information update failed", `${err.response.data.message}`, "bottomLeft");
              } else {
                openNotificationWithIcon("error", "Company information update failed",`${err.message}`, "bottomLeft");
              }
            });
          setIsModified(false);
          setEditMode(false);
        setCompanyDataLoading(true);
        getCompanySettingsData(currentCompanyInfo); 
      } catch (error) {
        setIsSaveButtonLoading(false);
        console.log('error in save function :', error)
      }
    } else {
      openNotificationWithIcon("error", "Company settings update failed", "No changes to save.", "bottomLeft");
    }
  };

  const handleEditToggle = () => {
    setEditMode(!editMode); // Toggle edit mode
    if(isModified){
      getCompanySettingsData(currentCompanyInfo);
      setIsModified(false);
    }
  };

  const getCompanySettingsData = async (companyInfo) => {
    if(!companyInfo) return;
    await CompanySettingsAPI.getCompanySettingsByUserDetailsId(companyInfo.userDetailsId, companyId, token).then((res) => {
      if (res?.data) {
        const {name = 'N/A', phoneNumber = "N/A", address = 'N/A', timezone = 'Asia/Colombo', themeColor = '#FFFF00', passwordExpirePeriod = 43} = res.data;
        firstForm.setFieldsValue({
          companyName: name,
          companyPhoneNumber: phoneNumber,
          address: address,
        })
        secondForm.setFieldsValue({
          timeZone: timezone,
          companyColor: themeColor,
        })
        thirdForm.setFieldsValue({
          passwordExpirePeriod: passwordExpirePeriod,
        })
      }
      setCompanyDataLoading(false)
    }).catch(err => {
      setCompanyDataLoading(false)
      console.log("error getting company data :", err.message);
    })
  };

  

  function validateFormData(data) {
    const validationResult = validateForm(data, validationRules)
    if (!validationResult.isValid) {
        const err = validationResult.errors
        const newErrors = {};
        Object.keys(err).forEach((value) => {
            newErrors[value] = err[value];
        })
        setErrors(prev => ({ ...prev, ...newErrors }));
    } else {
        const keyData = Object.keys(data)[0]
        setErrors(prev => ({ ...prev, [keyData]: '' }))
    }
  }

  return (
    <>
      <div style={{
        margin: '16px',
        padding: '8px 12px',
        background: colorBgContainer,
        borderRadius: borderRadiusLG,
      }}>
        <Flex justify='space-between' align='center'>
          <Flex justify='flex-start' align='center' gap={16}>
            {/* enable this only on mobile size  */}
            {/* <Button type="text" icon={<LeftOutlined />} /> */}
            <span>Company Information</span>
          </Flex>
          {
            !editMode && (
            <Button type="primary" onClick={handleEditToggle}>
              Edit
            </Button>
            )
          }
        </Flex>
      </div>
      {
        isCompanyDataLoading ? <CommonSkeletonLoader itemCount={3} /> : (
          <>
            <div style={{
              margin: '16px',
              padding: '24px 12px',
              background: colorBgContainer,
              borderRadius: borderRadiusLG,
            }}>

              <Form layout="vertical" variant='filled' form={firstForm} onValuesChange={() => setIsModified(true)}>
                <Row gutter={16}>
                  <Col lg={12} md={24} sm={24}>
                    <Form.Item label="Company Name" name="companyName">
                      <Input 
                        name="companyName" 
                        disabled 
                        // value={payload["companyName"]} 
                        placeholder='Company Name' 
                      />
                    </Form.Item>
                  </Col>
                  <Col lg={12} md={24} sm={24} validateStatus={errors.companyPhoneNumber ? 'error' : ''} help={errors.companyPhoneNumber || ''}>
                    <Form.Item label="Company Phone Number" name='companyPhoneNumber'
                      rules={[
                        {min: 9, max: 15, message: 'Mobile number must be between 9 and 15 digits.'},
                        {pattern: /^\d+$/, message: "Mobile number must contain only digits.",},
                      ]}
                    >
                      <Input 
                        disabled={!editMode} 
                        placeholder='Company Phone Number' 
                        variant='outlined'
                      />
                    </Form.Item>
                  </Col>
                  <Col lg={24} md={24} sm={24}>
                    <Form.Item label="Email" name='email'>
                      <Input 
                        // name="email" 
                        disabled 
                        // value={email} 
                        placeholder='Email' 
                      />
                    </Form.Item>
                  </Col>
                  <Col lg={24} md={24} sm={24}>
                    <Form.Item label="Address" name='address'
                      rules={[
                        {max: 250, message: 'Company Address must be less than 250 characters.'}
                      ]}
                    >
                      <TextArea 
                        // name="address" 
                        disabled={!editMode} 
                        // onChange={(e) => {
                        //   setPayload(prev => ({ ...prev, address: e.target.value})); 
                        //   setIsModified(true); 
                        // }}  
                        // value={payload["address"]} 
                        placeholder='Address' 
                        variant='outlined'
                        />
                    </Form.Item>
                  </Col>
                </Row>
              </Form>
            </div>
            <div style={{
              margin: '16px',
              padding: '8px 12px',
              background: colorBgContainer,
              borderRadius: borderRadiusLG,
            }}>
              <Flex justify='space-between' align='center'>
                <Flex justify='flex-start' align='center' gap={16}>
                  {/* enable this only on mobile size  */}
                  {/* <Button type="text" icon={<LeftOutlined />} /> */}
                  <span>Company Preferences</span>
                </Flex>
                {/* <Button type="primary" onClick={handleEditToggle}>
                  {editMode ? 'Cancel' : 'Edit'}
                </Button> */}
              </Flex>
            </div>
            <div style={{
              margin: '16px',
              padding: '24px 12px',
              background: colorBgContainer,
              borderRadius: borderRadiusLG,
            }}>

              <Form layout="vertical" variant='filled' form={secondForm} onValuesChange={() => setIsModified(true)}>
                <Row gutter={16}>
                  <Col lg={12} md={24} sm={24}>
                    <Form.Item label="Time Zone" name='timeZone'>
                      {/* <Input name="timeZone" disabled={!editMode} onChange={(e) => {setPayload(prev => ({ ...prev, timeZone: e.target.value})); setIsModified(true); }}  value={payload["timeZone"]} placeholder='Time Zone' /> */}
                      <Input 
                        placeholder='Time Zone' 
                        name="timeZone" 
                        disabled 
                        onChange={(e) => {
                          setPayload(prev => ({ ...prev, timeZone: e.target.value})); 
                          setIsModified(true); 
                        }}  
                      value={payload["timeZone"]} 
                      />
                    </Form.Item>
                  </Col>
                  <Col lg={12} md={24} sm={24}>
                    <Form.Item label="Company Color" name='companyColor'>
                    <ColorPicker 
                      name="companyColor" 
                      disabled 
                      value={payload["companyColor"]} 
                      onChange={(e) => {setPayload(prev => ({ ...prev, companyColor: e.toHex()})); setIsModified(true); }} 
                      presets={[{ label: 'System Colors', colors: presets, collapsed: true }]} 
                      defaultValue={colorPrimary} 
                    />
                    </Form.Item>
                  </Col>
                </Row>
              </Form>

              <Form layout="vertical" variant='filled' form={thirdForm} onValuesChange={() => setIsModified(true)}>
                <Row gutter={16}>
                  <Col lg={8} md={24} sm={24} >
                  <Form.Item
                    label="Password Expire Period"
                    name="passwordExpirePeriod"
                    rules={[
                      { required: true, message: 'Password expire period is required' },
                      {
                        pattern: /^\d+$/,
                        message: 'Password expire period must be a number',
                      },
                      {
                        validator: (_, value) => {
                          if (value === undefined || value === null || value === '') return Promise.resolve();
                          const num = Number(value);
                          if (num < 0) {
                            return Promise.reject('Password expire period cannot be negative');
                          }
                          if (num < 42 || num > 90) {
                            return Promise.reject('Password expire period must be between 42 and 90 days');
                          }
                          return Promise.resolve();
                        },
                      },
                    ]}
                  >
                    <Input
                      disabled={!editMode}
                      placeholder="Password Expire Period"
                      variant='outlined'
                    />
                  </Form.Item>

                    {/* <Form.Item 
                      name='passwordExpirePeriod'
                      label="Password Expire Period" 
                      validateStatus={errors.passwordExpirePeriod ? 'error' : ''} help={errors.passwordExpirePeriod || ''}
                    >
                      <Input 
                        disabled={!editMode} 
                        name='passwordExpirePeriod' 
                        onChange={(e) => { 
                          setPayload(prev => ({...prev, passwordExpirePeriod: e.target.value})); 
                          setIsModified(true); 
                          const validateData = {
                            passwordExpirePeriod: e.target.value
                          }; 
                          validateFormData(validateData); 
                        }} 
                        value={payload["passwordExpirePeriod"]} 
                        placeholder='Password Expire Period' 
                      />
                    </Form.Item> */}

                  </Col>
                </Row>
              </Form>
              {editMode && (
                <Flex align='center' justify='flex-end' gap={16}>
                  <Button type="default" onClick={handleCancelChange}>
                    Cancel
                  </Button>
                  <Button type="primary" onClick={handleSaveChange} disabled={!isModified} loading={isSaveButtonLoading}>
                    Save
                  </Button>
                </Flex>
              )}
            </div>
          </>
        )
      }
      {contextHolder}
    </>
  )
}
